import { generateEmailAttributionParameters } from 'modules/clienteling/pages/NewMessage/utils';
import { conversationServiceV2, attributionService } from 'config/api';
import { messageTypes, CLIENT_CREATION_MESSAGE_SUBJECTS } from 'constants/index';
import { isValidEmail, phoneNumberFormatter, uuidv4, isHTML, capitalizeString } from 'utils';

const updateContact = (contact) => (!isValidEmail(contact) ? phoneNumberFormatter(contact, 'plus1') : contact);

/**
 *
 * @param {Function} sendAttachedFile
 * @param {{}} imageSource
 * @param {[]} attachments
 * @returns {Promise<Awaited<unknown>[]|*[]>}
 */

/**
 *
 * @param {{}} imageSource
 * @param {{ id: string, item: { assetId: string, url: string }}[]} attachments
 * @returns {{}}
 */
const getImageUrls = (imageSource, attachments = []) => {
  const actualImageUrls = [];
  const thumbnailUrls = [];
  const attachmentUrls = [];

  imageSource.forEach((source) => {
    /**
     * new: `addCloudinaryAttachments` deprecated: `addAttachments`
     * new structure: { id: '', item: {} }
     * old structure: { actualImageUrl: '', thumbnailUrl: '' }
     */

    const actualImageUrl = source?.item?.actualImageUrl || source?.actualImageUrl;
    const thumbnailUrl = source?.item?.thumbnailUrl || source?.thumbnailUrl;

    if (actualImageUrl) {
      // `attachments` are files that the user selected
      const isAttachment = attachments.some(({ id }) => id === source.id);
      if (isAttachment) {
        attachmentUrls.push(actualImageUrl);
      } else {
        actualImageUrls.push(actualImageUrl);
        thumbnailUrls.push(thumbnailUrl || actualImageUrl);
      }
    }
  });

  // Add 'assets' as email attachments
  attachments.forEach((attachment) => {
    if (attachment.item?.assetId) {
      attachmentUrls.push(attachment.item.downloadUrl);
    }
  });

  return { actualImageUrls, thumbnailUrls, attachmentUrls };
};

export const sendMessage = async ({
  recipients,
  subject,
  message,
  messageType,
  associateId,
  associateContact,
  imageSource,
  attachments,
  postMutation,
  tags = {},
  templateAttributes = {},
  emailTemplate = '',
  preferredName,
  isComissionAttributionEnabled,
  isBgFarfetchEnabled,
}) => {
  const direction = 'Outbound';
  const campaignId = recipients.length > 1 ? uuidv4() : null;
  const trimmedMsg = message.trim();

  const getMessageBody = () => emailTemplate.trim() || trimmedMsg;
  const urls = getImageUrls(imageSource, attachments);

  try {
    const parameters = {
      InternalEntityId: associateId,
      InternalAddress: associateContact,
      ExternalEntities: recipients.map((r) => ({
        ExternalEntityId: updateContact(r.contact),
        ExternalAddress: updateContact(r.contact),
        ExternalEntityName: r.fullName,
      })),
      Direction: direction,
      MessageType: messageType,
      SourceId: 'OSP',
      MediaUrls: urls.actualImageUrls,
      ThumbnailUrls: urls.thumbnailUrls,
      Attachments: urls.attachmentUrls,
      CampaignId: campaignId,
      Tags: tags,
      Subject: subject,
      TemplateAttributes: templateAttributes,
      Body: getMessageBody(),
      PreferredName: capitalizeString(preferredName),
    };

    if (isComissionAttributionEnabled) {
      const attributionRecipientsEmails = parameters?.ExternalEntities?.map(({ ExternalAddress }) => ExternalAddress);
      const isEmptyLook = !parameters.Tags?.looksProducts?.length;
      const isEmptyCart = !parameters.Tags?.conciergeProducts?.length;

      if (attributionRecipientsEmails?.length && (!isEmptyLook || (!isEmptyCart && isBgFarfetchEnabled))) {
        const attributionParameters = generateEmailAttributionParameters({ parameters, attributionRecipientsEmails });
        attributionService.post('/products', attributionParameters);
      }
    }

    const { data } = await conversationServiceV2.post('/messages', [parameters], {
      httpClientOptions: { handleErrors: false },
    });
    const { result } = data;
    const { messageId } = result?.[0];
    const dateCreated = new Date(result?.[0]?.date_created);

    let messageBody = getMessageBody();
    messageBody = isHTML(messageBody) ? messageBody.replace(/(?:\r\n|\r|\n)/g, '<br>') : messageBody;
    const message = JSON.stringify({
      body: messageBody,
      mediaUrls: urls.actualImageUrls,
      templateAttributes,
    });
    const recipientsContact = recipients.map((r) => updateContact(r.contact));

    postMutation({
      variables: {
        message,
        messageId,
        associateId,
        contactId: recipientsContact,
        message_type: messageType,
        date_created: dateCreated,
        message_direction: direction,
        message_subject: subject,
      },
    });
  } catch (err) {
    return err;
  }
};

export const sendMessageWithAttributes = ({ user, config, customer, templateType, postMutation }) => {
  const commsHubConfig = config.get('commsHub');
  const { firstName, lastName, email } = customer;
  const { groupId, templates } = commsHubConfig[user.brand];
  const subjectText = CLIENT_CREATION_MESSAGE_SUBJECTS[user.brand][templateType];
  const templateAttributes = {
    SenderName: `${user.firstName} ${user.lastName}`,
    GroupId: groupId,
    TemplateId: templates[templateType],
  };

  if (templateType === 'cmdLinking') {
    templateAttributes.CMD = {
      customer: {
        first_name: firstName,
        last_name: lastName,
      },
    };
  }

  sendMessage({
    recipients: [
      {
        fullName: `${firstName} ${lastName}`,
        contact: email,
      },
    ],
    subject: subjectText,
    message: subjectText,
    messageType: messageTypes.email,
    associateId: user.employeeNumber,
    associateContact: user.email,
    imageSource: [],
    attachments: [],
    postMutation,
    templateAttributes,
  });
};

export const prepareNativeSmsMessageURL = (attributes) => {
  let messageBody = '';
  attributes.forEach((item) => {
    switch (true) {
      case item.isAsset:
      case item.isAttachment:
        messageBody += `${item.sharedProductLink}`;
        break;
      case item.isSharedLook:
      case item.isSharedProduct:
        messageBody += `${item.actualMessage}`;
        break;
      default:
        messageBody += ` ${item.actualMessage} `;
        break;
    }
  });

  return encodeURIComponent(messageBody);
};
